import { useState } from "react";
import { Play } from "lucide-react";
import { GALLERY, GALLERY_CATEGORIES } from "@/lib/data";
import { cn } from "@/lib/utils";

export default function GalleryPage() {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? GALLERY : GALLERY.filter((g) => g.category === active);

  return (
    <div>
      <section className="py-16 lg:py-24 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-semibold tracking-[0.18em] uppercase text-[#081537] mb-3">Gallery</div>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#081537] text-balance max-w-3xl">
            See our crew in action.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Real moves, real care. A glimpse of the precision and professionalism we bring to every assignment.
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`gallery-tab-${c.toLowerCase().replace(/\s+/g, "-").replace("&", "and")}`}
                className={cn(
                  "px-5 py-2 rounded-full text-sm font-medium border transition-all",
                  active === c
                    ? "bg-[#081537] text-white border-[#081537]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#081537]"
                )}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((g, i) => (
              <GalleryItem key={`${g.url}-${i}`} item={g} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function GalleryItem({ item, index }) {
  const [playing, setPlaying] = useState(false);

  if (item.type === "video") {
    return (
      <div data-testid={`gallery-item-${index}`} className="relative overflow-hidden rounded-2xl group bg-black">
        {playing ? (
          <video
            src={item.url}
            poster={item.poster}
            controls
            autoPlay
            muted
            playsInline
            className="w-full h-72 object-cover"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            data-testid={`gallery-video-play-${index}`}
            className="block w-full h-72 relative"
            aria-label="Play video"
          >
            <img
              src={item.poster}
              alt={item.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors grid place-items-center">
              <div className="w-16 h-16 rounded-full bg-white/95 text-[#081537] grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 fill-current ml-1" />
              </div>
            </div>
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#081537] text-white text-[10px] uppercase tracking-wider font-semibold">
              Video
            </div>
            <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
              <div className="text-xs uppercase tracking-wider text-white/70 font-semibold">{item.category}</div>
              <div className="text-white font-medium text-sm">{item.alt}</div>
            </div>
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`gallery-item-${index}`} className="relative overflow-hidden rounded-2xl group">
      <img src={item.url} alt={item.alt} className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-300 font-semibold">{item.category}</div>
          <div className="text-white font-medium">{item.alt}</div>
        </div>
      </div>
    </div>
  );
}
